/**
 * @title Configuracao Global de Cores
 * @description Configure as cores disponiveis para todos os seletores de produto do site
 */
export interface ColorMapping {
  /** @title Nome da Cor */
  colorName: string;
  /** @title Codigo Hexadecimal */
  hexCode: string;
}

const canonicalizeColorName = (colorName: string): string => {
  return colorName.trim().toLocaleUpperCase("pt-BR");
};

/**
 * @title Mapeamento Global de Cores
 * @description Configuracao centralizada das cores para seletores de produto (baseado em colors.csv)
 */
export const DEFAULT_COLOR_MAPPING: ColorMapping[] = [
  // Cores principais
  { colorName: "BRANCO", hexCode: "#FDFDFD" },
  { colorName: "PRETO", hexCode: "#101010" },
  { colorName: "AMARELO", hexCode: "#FFE064" },
  { colorName: "LARANJA", hexCode: "#FF914D" },
  { colorName: "CORAL", hexCode: "#FF6D6D" },
  { colorName: "VERMELHO", hexCode: "#F13030" },
  { colorName: "ROSA", hexCode: "#ED6C9A" },
  { colorName: "ROXO", hexCode: "#A856AF" },
  { colorName: "AZUL-MARINHO", hexCode: "#0D358D" },
  { colorName: "AZUL", hexCode: "#509CEB" },
  { colorName: "TURQUESA", hexCode: "#85DCDF" },
  { colorName: "VERDE", hexCode: "#87CB98" },
  { colorName: "MARROM", hexCode: "#4B3125" },
  { colorName: "BEGE", hexCode: "#C6BCB5" },
  { colorName: "NUDE", hexCode: "#DFDDCF" },
  { colorName: "CINZA", hexCode: "#9D9C9C" },
  { colorName: "VINHO", hexCode: "#600E2E" },

  // Variacoes -2
  { colorName: "BRANCO-2", hexCode: "#FFF9F6" },
  { colorName: "PRETO-2", hexCode: "#050404" },
  { colorName: "AMARELO-2", hexCode: "#E8C434" },
  { colorName: "LARANJA-2", hexCode: "#FF751F" },
  { colorName: "CORAL-2", hexCode: "#CF6464" },
  { colorName: "VERMELHO-2", hexCode: "#B61D1D" },
  { colorName: "ROSA-2", hexCode: "#DD50A4" },
  { colorName: "ROXO-2", hexCode: "#6F3796" },
  { colorName: "VIOLETA-2", hexCode: "#8C52FF" },
  { colorName: "AZUL-2", hexCode: "#356AAF" },
  { colorName: "TURQUESA-2", hexCode: "#0097B2" },
  { colorName: "VERDE-2", hexCode: "#4A9D75" },
  { colorName: "MARROM-2", hexCode: "#6D3E21" },
  { colorName: "BEGE-2", hexCode: "#C4AE95" },
  { colorName: "NUDE-2", hexCode: "#E3DEDC" },
  { colorName: "CINZA-2", hexCode: "#545352" },

  // Variacoes -3
  { colorName: "AMARELO-3", hexCode: "#E6D459" },
  { colorName: "LARANJA-3", hexCode: "#CF6320" },
  { colorName: "CORAL-3", hexCode: "#CD6262" },
  { colorName: "VERMELHO-3", hexCode: "#D10909" },
  { colorName: "ROSA-3", hexCode: "#EDC4D3" },
  { colorName: "ROXO-3", hexCode: "#6F4382" },
  { colorName: "VIOLETA-3", hexCode: "#8F61EB" },
  { colorName: "AZUL-3", hexCode: "#6F85C8" },
  { colorName: "TURQUESA-3", hexCode: "#43B4B8" },
  { colorName: "VERDE-3", hexCode: "#50846B" },
  { colorName: "MARROM-3", hexCode: "#2E1F0F" },
  { colorName: "BEGE-3", hexCode: "#DBCFCA" },
  { colorName: "NUDE-3", hexCode: "#DBD3C1" },
  { colorName: "CINZA-3", hexCode: "#706C64" },
];

/**
 * Funcao para normalizar o nome da cor (remove numeros e tracos)
 * Mantida para compatibilidade, mas agora tambem normaliza o casing
 */
export const normalizeColorName = (colorName: string): string => {
  return canonicalizeColorName(colorName).replace(/-\d+$/, "").trim();
};

/**
 * Funcao para obter a cor baseada no nome
 * Primeiro procura pela cor exata (com numero), depois pelo nome normalizado
 */
export const getColorByName = (
  colorName: string,
  colorMapping: ColorMapping[] = DEFAULT_COLOR_MAPPING,
): string => {
  const colorMappingRecord: Record<string, string> = colorMapping.reduce(
    (acc, { colorName, hexCode }) => {
      acc[canonicalizeColorName(colorName)] = hexCode;
      return acc;
    },
    {} as Record<string, string>,
  );

  const normalizedInput = canonicalizeColorName(colorName);

  // Primeira tentativa: buscar cor exata (ex: ROSA-3)
  if (colorMappingRecord[normalizedInput]) {
    return colorMappingRecord[normalizedInput];
  }

  // Segunda tentativa: buscar cor normalizada (ex: ROSA-3 -> ROSA)
  const normalizedName = normalizeColorName(normalizedInput);
  if (colorMappingRecord[normalizedName]) {
    return colorMappingRecord[normalizedName];
  }

  // Cor padrao se nao encontrar
  return "#CCCCCC";
};
